import React from 'react';
import { Platform, StyleSheet, type TextStyle } from 'react-native';
import { Text } from './Texte';
import MaskedView from '@react-native-masked-view/masked-view';
import { LinearGradient } from 'expo-linear-gradient';
import { gradients } from '../theme/tokens';

export interface GradientTextProps {
  children: string;
  style?: TextStyle | TextStyle[];
  // Teintes du dégradé. Par défaut celui de la marque ; `gradients.surNuit` pour un fond sombre.
  colors?: readonly [string, string, ...string[]];
  // Pour un mot qui doit tenir sur une ligne : la police se réduit au lieu de passer à la ligne.
  numberOfLines?: number;
  adjustsFontSizeToFit?: boolean;
  minimumFontScale?: number;
  testID?: string;
}

// Applique le dégradé charte sur un texte (clippé via masked-view).
// À utiliser comme BLOC (sa propre ligne), pas en plein milieu d'un mot.
export function GradientText({
  children,
  style,
  colors = gradients.brand,
  numberOfLines,
  adjustsFontSizeToFit,
  minimumFontScale,
  testID,
}: GradientTextProps) {
  const textStyle = StyleSheet.flatten([style]);
  const ajustement = { numberOfLines, adjustsFontSizeToFit, minimumFontScale };

  // MaskedView n'existe pas sur le web : le texte s'y affichait dans sa couleur par défaut, noir,
  // donc invisible sur un fond bleu nuit. Le web peint le texte avec un dégradé CSS rogné à ses
  // lettres (`background-clip: text`), ce que react-native-web transmet tel quel.
  if (Platform.OS === 'web') {
    const css = {
      backgroundImage: `linear-gradient(90deg, ${colors.join(', ')})`,
      backgroundClip: 'text',
      color: 'transparent',
    } as unknown as TextStyle;
    return (
      <Text testID={testID} style={[textStyle, css]} {...ajustement}>
        {children}
      </Text>
    );
  }

  return (
    <MaskedView maskElement={<Text testID={testID} style={textStyle} {...ajustement}>{children}</Text>}>
      <LinearGradient colors={colors} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
        {/* Copie invisible : dimensionne le dégradé sur les bornes du texte. */}
        <Text style={[textStyle, styles.sizer]} {...ajustement}>{children}</Text>
      </LinearGradient>
    </MaskedView>
  );
}

const styles = StyleSheet.create({
  sizer: { opacity: 0 },
});
